import React from "react";
import styled from "styled-components";

import MarkdownContent from "../../../components/markdown/MarkdownContent";
import { pickLang } from "../utils";
import type { ProfileLanguage, ProfileTextBlock } from "../../../types/profile";
import SectionTitle from "./SectionTitle";

export interface ProfileTextSectionProps {
  blocks: ProfileTextBlock[];
  language: ProfileLanguage;
}

/**
 * プロフィールテキスト（Django `.profile__profile` 準拠）。
 *
 * - 見出し（section_name）は `SectionTitle`（共通）を使用：上に区切り線、
 *   clamp フォント、line-height 200%。
 * - 本文（Markdown）は `MarkdownContent` の document バリアントで描画する。
 *   タイポグラフィ（フォントサイズ・行間・要素余白）はバリアント側に集約し、
 *   ここではレイアウト（中央寄せ・max-width 640px・両端揃え）のみを持つ。
 */
const Block = styled.div`
  width: 100%;
  padding: 16px 0 0;
`;

const Quote = styled.div`
  max-width: 640px;
  margin: 32px auto 96px;
  /* text-align は継承されるため、本文ブロック（p 等）も両端揃えになる */
  text-align: justify;
`;

const ProfileTextSection: React.FC<ProfileTextSectionProps> = ({ blocks, language }) => {
  const visible = blocks.filter((b) =>
    pickLang(b.content_jp, b.content_en, language).trim(),
  );
  if (visible.length === 0) return null;

  return (
    <section className="u-w100 u-col u-pt32 u-pb32">
      {visible.map((block) => {
        const sectionName = pickLang(block.section_name_jp, block.section_name_en, language);
        const content = pickLang(block.content_jp, block.content_en, language);
        return (
          <Block key={block.id}>
            {sectionName && <SectionTitle className="u-w100">{sectionName}</SectionTitle>}
            <Quote>
              <MarkdownContent content={content} variant="document" breaks />
            </Quote>
          </Block>
        );
      })}
    </section>
  );
};

export default ProfileTextSection;
